import {
    calendarDateInZone,
    calendarDateSchema,
    familyRoleAtLeast,
    shiftCalendarDate,
    zonedDayBounds,
} from '@baby-tracker/validation';
import { differenceInDays, differenceInMonths } from 'date-fns';
import { Baby } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router';

import { routePaths } from '../app/route-paths.js';
import { DayStepper } from '../components/features/tracking/DayStepper.js';
import { QuickLog } from '../components/features/tracking/QuickLog.js';
import { SleepRhythm } from '../components/features/tracking/SleepRhythm.js';
import { RecentActivity } from '../components/features/tracking/RecentActivity.js';
import { TimerBanner } from '../components/features/tracking/TimerBanner.js';
import styles from '../components/features/tracking/tracking.module.css';
import { WeekChart } from '../components/features/tracking/WeekChart.js';
import { NotificationList } from '../components/features/reminders/NotificationList.js';
import { PageHeader } from '../components/layout/PageHeader.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { errorText } from '../components/ui/error-text.js';
import { ErrorState } from '../components/ui/ErrorState.js';
import { Spinner } from '../components/ui/Spinner.js';
import { useActiveChild } from '../hooks/useActiveChild.js';
import { useNow } from '../hooks/useNow.js';
import { useActiveFamily } from '../hooks/useActiveFamily.js';
import { useChildren } from '../hooks/useChildren.js';
import { useFamily } from '../hooks/useFamily.js';
import { useMarkNotificationRead, useNotifications } from '../hooks/useMedia.js';
import { useUpdateReminder } from '../hooks/useRecords.js';
import { useDayRollups, useDaySummary, useTimeline } from '../hooks/useTracking.js';
import { summaryLine } from '../lib/activity-format.js';
import { formatCalendarDate } from '../lib/labels.js';

function ageLabel(dateOfBirth: string): string {
    const birth = new Date(`${dateOfBirth}T00:00:00`);
    const months = differenceInMonths(new Date(), birth);
    if (months < 1) {
        const days = Math.max(differenceInDays(new Date(), birth), 0);
        return days === 1 ? '1 day old' : `${days} days old`;
    }
    if (months < 24) {
        return months === 1 ? '1 month old' : `${months} months old`;
    }
    const years = Math.floor(months / 12);
    return years === 1 ? '1 year old' : `${years} years old`;
}

export function TodayPage(): ReactElement {
    const { active, me } = useActiveFamily();
    const family = useFamily(active?.id);
    const children = useChildren(active?.id, false);
    const [params, setParams] = useSearchParams();
    const today = family.data ? calendarDateInZone(new Date(), family.data.timeZone) : undefined;
    const requested = calendarDateSchema.safeParse(params.get('date'));
    const selected =
        today === undefined
            ? undefined
            : requested.success && requested.data <= today
              ? requested.data
              : today;
    const summary = useDaySummary(active?.id, selected);
    const chartFrom = selected === undefined ? undefined : shiftCalendarDate(selected, -6);
    const rollups = useDayRollups(active?.id, chartFrom, selected);
    const dayBounds =
        selected !== undefined && family.data
            ? zonedDayBounds(selected, family.data.timeZone)
            : undefined;
    const dayTimeline = useTimeline(active?.id, dayBounds?.from, dayBounds?.to);
    const { activeChild, setActiveChildId } = useActiveChild(children.data);
    const notifications = useNotifications(active?.id);
    const markRead = useMarkNotificationRead(active?.id ?? '');
    const updateReminder = useUpdateReminder(active?.id ?? '');
    const canLog = active !== undefined && familyRoleAtLeast(active.role, 'caregiver');
    const viewingToday = selected !== undefined && selected === today;
    const now = useNow(viewingToday);
    const sleepFromDate = selected === undefined ? undefined : shiftCalendarDate(selected, -1);
    const sleepFrom =
        sleepFromDate !== undefined && family.data !== undefined
            ? zonedDayBounds(sleepFromDate, family.data.timeZone).from
            : undefined;
    const sleepTimeline = useTimeline(
        active?.id,
        sleepFrom,
        dayBounds?.to,
        activeChild?.id === undefined
            ? { type: 'sleep' }
            : { type: 'sleep', childId: activeChild.id },
    );
    const activeSummary = summary.data?.children.find((entry) => entry.childId === activeChild?.id);
    const subtitle =
        selected === undefined
            ? undefined
            : active
              ? `${formatCalendarDate(selected)} · ${active.name}`
              : formatCalendarDate(selected);
    const loadError = family.error ?? children.error ?? summary.error;
    const loading =
        me.isPending ||
        family.isLoading ||
        children.isLoading ||
        (selected !== undefined && summary.isLoading);

    function chooseDate(date: string): void {
        const next = new URLSearchParams(params);
        if (date === today) {
            next.delete('date');
        } else {
            next.set('date', date);
        }
        setParams(next, { replace: true });
    }

    return (
        <>
            <PageHeader title="Today" subtitle={subtitle} />
            {selected !== undefined && today !== undefined ? (
                <DayStepper date={selected} today={today} onChange={chooseDate} />
            ) : null}
            {viewingToday && canLog && family.data && summary.data ? (
                <TimerBanner
                    familyId={family.data.id}
                    volumeUnit={family.data.unitVolume}
                    openFeeding={activeSummary?.openFeeding ?? null}
                    openSleep={activeSummary?.openSleep ?? null}
                    openPump={summary.data.openPump}
                />
            ) : null}
            {loading ? <Spinner label="Loading today" /> : null}
            {loadError ? (
                <ErrorState title="Could not load today" description={errorText(loadError)} />
            ) : null}
            {children.data?.length === 0 ? (
                <EmptyState
                    icon={Baby}
                    title="No child profiles yet"
                    description="Add a child to start tracking feeds, sleep and nappies."
                    action={
                        active?.role === 'owner' ? (
                            <Link to={routePaths.settings}>Add a child</Link>
                        ) : undefined
                    }
                />
            ) : null}
            {(children.data?.length ?? 0) > 1 ? (
                <div className={styles.children} role="group" aria-label="Children">
                    {children.data?.map((child) => (
                        <Button
                            key={child.id}
                            variant={child.id === activeChild?.id ? 'primary' : 'secondary'}
                            aria-pressed={child.id === activeChild?.id}
                            onClick={() => {
                                setActiveChildId(child.id);
                            }}
                        >
                            {child.name}
                        </Button>
                    ))}
                </div>
            ) : null}
            {notifications.data && active ? (
                <NotificationList
                    items={notifications.data}
                    pending={markRead.isPending || updateReminder.isPending}
                    onRead={(id) => {
                        markRead.mutate(id);
                    }}
                    onDisable={
                        canLog
                            ? (notificationId, reminderId) => {
                                  updateReminder.mutate(
                                      { id: reminderId, body: { enabled: false } },
                                      {
                                          onSuccess: () => {
                                              markRead.mutate(notificationId);
                                          },
                                      },
                                  );
                              }
                            : undefined
                    }
                />
            ) : null}
            <div className={styles.columns}>
                <div className={styles.recent}>
                    {children.data?.map((child) => {
                        const row = summary.data?.children.find(
                            (entry) => entry.childId === child.id,
                        );
                        const isActive = child.id === activeChild?.id;
                        return (
                            <Card key={child.id} title={child.name} headingLevel={2}>
                                <p className={styles.summary}>{ageLabel(child.dateOfBirth)}</p>
                                <p className={styles.summary}>
                                    {row && family.data
                                        ? summaryLine(row, family.data.unitVolume)
                                        : 'Nothing logged yet'}
                                </p>
                                {isActive && family.data && selected !== undefined ? (
                                    <SleepRhythm
                                        date={selected}
                                        timeZone={family.data.timeZone}
                                        now={now}
                                        sleeps={(sleepTimeline.data ?? []).flatMap((item) =>
                                            item.type === 'sleep' && item.sleep.childId === child.id
                                                ? [
                                                      {
                                                          startAt: item.sleep.startAt,
                                                          endAt: item.sleep.endAt,
                                                      },
                                                  ]
                                                : [],
                                        )}
                                        pending={sleepTimeline.isLoading}
                                        error={sleepTimeline.error}
                                    />
                                ) : null}
                                {isActive && viewingToday && canLog && family.data ? (
                                    <QuickLog
                                        familyId={family.data.id}
                                        childId={child.id}
                                        childName={child.name}
                                        volumeUnit={family.data.unitVolume}
                                        massUnit={family.data.unitMass}
                                        timeZone={family.data.timeZone}
                                        timeline={dayTimeline.data ?? []}
                                        openFeeding={row?.openFeeding ?? null}
                                        openSleep={row?.openSleep ?? null}
                                        openPump={summary.data?.openPump ?? null}
                                    />
                                ) : null}
                            </Card>
                        );
                    })}
                </div>
                <div className={styles.recent}>
                    <WeekChart
                        days={rollups.data}
                        childId={activeChild?.id}
                        error={rollups.error}
                    />
                    {family.data && selected !== undefined && !dayTimeline.isLoading ? (
                        <RecentActivity
                            familyId={family.data.id}
                            items={dayTimeline.data ?? []}
                            children={children.data ?? []}
                            volumeUnit={family.data.unitVolume}
                            massUnit={family.data.unitMass}
                            timeZone={family.data.timeZone}
                            date={selected}
                            canLog={canLog}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
}
